import React, {Component, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    CsvExportModule,
    ExcelExportModule,
    SetFilterModule,
} from "ag-grid-enterprise";
import {AllCommunityModule, ClientSideRowModelModule} from "ag-grid-community";
import {Helmet} from "react-helmet";
import {
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    getUseFilterStateInSession,
    onColumnStateChangedHelper,
    onFilterChangedHelper,
    onGridReadyHelper,
    onGridReadyHelperForColumnState,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper,
    updateUseFilterStateHelper
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import CustomNameCellEditor from "../../utils/customCellEditor";
import DTPicker, {dateFilterParametersInHeaderSuppressSorting} from "../../utils/DTPicker";
import {GridColumnFilterStateSaving} from "../../components/columnfilterComponent";
import {
    loadDataWithSSEAndStartChangeStreamListener,
    standardHandleInsertEvent,
    standardHandlePopulateGrid,
    standardHandleUpdateAndReplaceEvent
} from "../../utils/sseAndChangeStreamHelper";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {standardExcelExportHelper, standardExcelExportObjectInContextMenu} from "../../utils/excelExportHelper";
import {
    ClickToShowColumnOptionsWithToggleButtonGroup,
    customColumnModeText,
    mediumColumnModeText,
    minColumnModeText,
    standardApplyMinimumOrMediumColumnMode
} from "../../components/clickToShowButtons";
import {defaultClientSideTextFilterParams} from "../../utils/filterHelper";
import {Link, useLocation, useOutletContext} from "react-router-dom";
import {getDemoModeSetting, getTrainingModeSetting} from "../../utils/trainingAndDemoModeHelper";
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import {agentDisplayNameAndZenGroupDisplayNameOnlyCellEditingStopped} from "../../utils/gridCellEditing";
import {defaultAgentNameColumnInitWithOptions} from "../../utils/agentNameGridHelper";
import {MuiIconButtonWithTooltip} from "../../components/muiComponents";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {defaultZenGroupColumnInitWithOptionsWithValueGetter} from "../../utils/zenGroupDisplayNameGridHelper";
import {checkIsUsingRememberMe} from "../../utils/storageHelper";
import {dllRelatedProcessesExists} from "../api/relatedModuleLoadsApi";
import {DisplayOptions} from "../../components/displayOptionsComponent";

let gridColumnStateSessionVariableName = "relatedDLLGridColumnState"
let gridFilterStateSessionVariableName = "relatedDLLGridFilterState"
let minColumnIds = ["machineName", "path", "trustError", "dateCreated"]
let medColumnIds = ["zenGroupDisplayName", "agentDisplayName", "machineName", "path", "trustError", "dateCreated"]
export default function RelatedModuleLoads() {
    const { isLoading, setIsLoading } = useOutletContext();
    const [gridApi, setGridApi] = useState(null);
    const location = useLocation();
    const [useFilterStateSettingToggled, setUseFilterStateSettingToggled] = useState(getUseFilterStateInSession(gridFilterStateSessionVariableName));
    const [useColumnStateSettingToggled, setUseColumnStateSettingToggled] = useState(getUseColumnStateInSession(gridColumnStateSessionVariableName));
    const [columnMode, setColumnMode] = useState(getColumnModeInSession(gridColumnStateSessionVariableName));
    const [sseDataPullActive, setSSEDataPullActive] = useState(true);
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200); //200 to start for the initial sse data pull, will change when sse data pull is done for change streams
    // eslint-disable-next-line no-unused-vars
    const [columnDefs, setColumnDefs] = useState([
        defaultZenGroupColumnInitWithOptionsWithValueGetter(true,true, true),
        defaultAgentNameColumnInitWithOptions(false, 1),
        { field: "machineName", headerName: "Machine Name", initialWidth: 225,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 1,
            },
            cellRenderer:function(params){
                let agentLinkDiv = ""
                if(params.data.agentId){
                    //only using agentId for cross-link filter now so only need to check agentId exists
                    agentLinkDiv =
                        <Link to={{pathname:"/private/agents"}} state={{agentDisplayNameClicked: params.node.data.agentDisplayName, zenGroupIdClicked: params.node.data.zenGroupId,
                            agentIdClicked : params.node.data.agentId, machineNameClicked: params.node.data.machineName}} className="">
                            <MuiIconButtonWithTooltip
                                icon={
                                    <FontAwesomeIcon
                                        className="object-contain"
                                        icon="fa-duotone fa-user-gear"
                                        size="xs"
                                    />
                                }
                                tooltipTitle={"Click to Manage This Agent"}
                                tooltipPlacement={"bottom-start"}
                            />
                        </Link>
                }
                return(
                    <div className={"flex flex-nowrap items-center gap-x-2"}>
                        {agentLinkDiv}
                        {params.value}
                    </div>
                )
            },
            sortable: true,
        },
        { field: "path", headerName: "Path", initialWidth: 600,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true
        },
        { field: "trustError", headerName: "Trust Error", initialWidth: 275,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['UNSIGNED', 'SIGNER_NOT_WHITELISTED', 'TAMPERED_IN_MEMORY', 'TAMPERED_ON_DISK', 'GENERIC_ERROR', 'INVALID_ERROR'],
                valueFormatter: function (params) {
                    if(params.value!=="(Select All)"){
                        return convertDllTrustError(params.value)
                    }
                    else{
                        return params.value;
                    }
                },
                suppressSorting: false,
                convertValuesToStrings: true
            },
            sortable: true,
            valueFormatter: function (params) {
                return convertDllTrustError(params.value)
            },
        },
        { field: "trustedForDataIncidents", headerName: "Trusted for Extortion Responses", initialWidth: 330,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['Trusted', 'Untrusted'],
                suppressSorting: true,
                convertValuesToStrings: true
            },
            valueGetter: (params) => {
                if(params.node.data.trustedForDataIncidents){
                    return "Trusted"
                }
                else{
                    return "Untrusted"
                }
            },
            sortable: true
        },
        { field: "trustedForIdentityIncidents", headerName: "Trusted for Identity Accesses", initialWidth: 330,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['Trusted', 'Untrusted'],
                suppressSorting: true,
                convertValuesToStrings: true
            },
            valueGetter: (params) => {
                if(params.node.data.trustedForIdentityIncidents){
                    return "Trusted"
                }
                else{
                    return "Untrusted"
                }
            },
            sortable: true
        },
        {
            field: "rootCauseAnalysis", headerName: "Root Cause Analysis", width: 300,
            sortable: false,
            valueFormatter: function (params) {
                if (params.node.data.rootCauseAnalysis) {
                    return params.node.data.rootCauseAnalysis
                }
                if (params.node.data.zenGroupId && params.node.data.agentId && params.node.data.uniqueProcessIdentifier !== null && params.node.data.uniqueProcessIdentifier !== undefined) {
                    dllRelatedProcessesExists(params.node.data.zenGroupId, params.node.data.agentId, params.node.data.uniqueProcessIdentifier).then(response => {
                        params.node.data.relatedProcessCreationExists = response.relatedProcessCreationExists
                        params.node.data.relatedProcessInjectionExists = response.relatedProcessInjectionExists
                        //Show the Related Processes Found text over Memory Download Available if either fields are true
                        if (response.relatedProcessCreationExists || response.relatedProcessInjectionExists) {
                            params.node.setDataValue("rootCauseAnalysis", "Related Processes Found")
                        } else {
                            params.node.setDataValue("rootCauseAnalysis", " ") //lets cell know not to call checkIncidentHasRelatedProcessInjectionsAndCreationsReactive again
                        }
                        //childUsername formatting
                        if(response.processCreationChildUsername !== null && response.processCreationChildUsername !== undefined && response.processCreationChildUsername.trim().length > 0){
                            params.node.setDataValue("childUsername", response.processCreationChildUsername) //update the childUsername col
                        }
                        else{
                            params.node.setDataValue("childUsername", " ") //update the childUsername col
                        }
                    }).catch(function (error) {
                        params.node.setDataValue("rootCauseAnalysis", " ") //lets cell know not to call checkIncidentHasRelatedProcessInjectionsAndCreationsReactive again
                        params.node.setDataValue("childUsername", " ") //update the childUsername col

                    })
                    params.node.data.rootCauseAnalysis = params.node.data.zenGroupId
                    return params.node.data.zenGroupId
                }
            },
            cellRenderer: function (params) {
                let spinnerDiv = ""
                let processCreationsLinkDiv = ""
                let processInjectionsLinkDiv = ""
                if (params.node.data.zenGroupId && params.node.data.agentId && params.node.data.uniqueProcessIdentifier !== null && params.node.data.uniqueProcessIdentifier !== undefined){
                    if (params.node.data.rootCauseAnalysis === params.node.data.zenGroupId) {
                        spinnerDiv = <FontAwesomeIcon
                            className="contain fa-pulse mr-1"
                            icon="fa-light fa-spinner"
                            size="lg"
                            name="relatedProcessesLoading"
                        />
                    } else {
                        spinnerDiv = ""
                    }
                    //Links
                    if (params.node.data.relatedProcessCreationExists === true) {
                        processCreationsLinkDiv =
                            <div className={"mb-1"}>
                                <MuiIconButtonWithTooltip
                                    icon={
                                        <FontAwesomeIcon
                                            className="object-contain"
                                            icon="fa-duotone fa-magnifying-glass-arrow-right"
                                            size="xs"
                                        />
                                    }
                                    tooltipTitle={"Click to view related process creations"}
                                    tooltipPlacement={"bottom-start"}
                                    onClick={() => {
                                        let baseUrl = "/private/processCreations";
                                        let urlSearchParams = new URLSearchParams();
                                        urlSearchParams.append("uniqueProcessIdentifier", JSON.stringify(params.node.data.uniqueProcessIdentifier));
                                        urlSearchParams.append("zenGroupId", JSON.stringify(params.node.data.zenGroupId));

                                        let urlWithParams = `${baseUrl}?${urlSearchParams.toString()}`;
                                        let rememberMeEnabled = checkIsUsingRememberMe()
                                        if(rememberMeEnabled){
                                            //for now, if using rem me then open in new tab
                                            window.open(urlWithParams, "_blank", "noreferrer");
                                        }
                                        else{
                                            //for now, if not using rem me then we have to open in same tab
                                            window.open(urlWithParams, "_self", "noreferrer");
                                        }
                                    }}
                                />
                            </div>
                    }
                    if (params.node.data.relatedProcessInjectionExists === true) {
                        processInjectionsLinkDiv =
                            <div className={"mb-1"}>
                                <MuiIconButtonWithTooltip
                                    icon={
                                        <FontAwesomeIcon
                                            className="object-contain"
                                            icon="fa-duotone fa-syringe"
                                            size="xs"
                                        />
                                    }
                                    tooltipTitle={"Click to view related process injections"}
                                    tooltipPlacement={"bottom-start"}
                                    onClick={() => {
                                        let baseUrl = "/private/processInjections";
                                        let urlSearchParams = new URLSearchParams();
                                        urlSearchParams.append("uniqueProcessIdentifier", JSON.stringify(params.node.data.uniqueProcessIdentifier));
                                        urlSearchParams.append("zenGroupId", JSON.stringify(params.node.data.zenGroupId));

                                        let urlWithParams = `${baseUrl}?${urlSearchParams.toString()}`;
                                        let rememberMeEnabled = checkIsUsingRememberMe()
                                        if(rememberMeEnabled){
                                            //for now, if using rem me then open in new tab
                                            window.open(urlWithParams, "_blank", "noreferrer");
                                        }
                                        else{
                                            //for now, if not using rem me then we have to open in same tab
                                            window.open(urlWithParams, "_self", "noreferrer");
                                        }
                                    }}
                                />
                            </div>
                    }
                }

                return (
                    <div className={"flex flex-nowrap items-center justify-start gap-x-1"}>
                        {spinnerDiv}
                        {processCreationsLinkDiv}
                        {processInjectionsLinkDiv}
                        {params.node.data.rootCauseAnalysis}
                    </div>
                )
            }
        },
        { field: "childUsername", headerName: "Username", initialWidth: 320,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],

                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true,
            valueFormatter: function (params) {
                if (params.node.data.childUsername) {
                    return params.node.data.childUsername
                }
                if (params.node.data.zenGroupId && params.node.data.agentId && params.node.data.uniqueProcessIdentifier !== null && params.node.data.uniqueProcessIdentifier !== undefined) {
                    //the rootCauseAnalysis col takes care of populating this value
                    params.node.data.childUsername = params.node.data.zenGroupId
                    return params.node.data.zenGroupId
                }
            },
            cellRenderer: function (params) {
                let spinnerDiv = ""
                if (params.node.data.zenGroupId && params.node.data.agentId && params.node.data.uniqueProcessIdentifier !== null && params.node.data.uniqueProcessIdentifier !== undefined){
                    if (params.node.data.childUsername === params.node.data.zenGroupId) {
                        spinnerDiv = <FontAwesomeIcon
                            className="contain fa-pulse mr-1"
                            icon="fa-light fa-spinner"
                            size="lg"
                            name="childUsernameLoading"
                        />
                    } else {
                        spinnerDiv = ""
                    }
                }

                return (
                    <div className={"flex flex-nowrap items-center justify-start gap-x-1"}>
                        {spinnerDiv}
                        {params.node.data.childUsername}
                    </div>
                )
            }
        },
        { field: "dateCreated", headerName: "Date Created", width: 300,
            filter: 'agDateColumnFilter',
            filterParams:dateFilterParametersInHeaderSuppressSorting,
            sortable: true,
            valueFormatter: dateValueFormatter
        },
        { field: "dateCollected", headerName: "Date Collected", width: 300,
            filter: 'agDateColumnFilter',
            filterParams:dateFilterParametersInHeaderSuppressSorting,
            sortable: true,
            valueFormatter: dateValueFormatter
        },
    ])
    const [defaultColDef, setDefaultColDef] = useState(
        {
            resizable: true,
            filterParams: null,
            floatingFilter: true,
            headerClass: "border-0 border-b-0",
            cellClass: "outline:none",
            enableCellChangeFlash: true,
            autoHeight: false,
            cellDataType: false //disable inferring cell data type automatically, can be overridden in individual colDef
        }
    )
    const sideBar = useMemo(() => {
        //Inside useMemo to help prevent the sidebar from re-rendering
        return getDefaultAgGridSidebarProps(260)
    }, []);
    const rowSelection = useMemo(() => {
        return {
            mode: 'singleRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
        };
    }, []);
    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Related Untrusted Module Loads</title>
                <script src="https://js.stripe.com/v3/"/>
                <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap" rel="stylesheet"/>
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-2 h-full">
                {privatePageHeaderHelper("Related Untrusted Module Loads")}
                <hr className="h-0.5" />
                <div className="flex flex-col justify-between gap-x-1 gap-y-3">
                    <div className={"self-end flex flex-col gap-y-3 w-full"}>
                        <DisplayOptions useFilterStateSettingToggled={useFilterStateSettingToggled}
                                        setUseFilterStateSettingToggled={setUseFilterStateSettingToggled}
                                        toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                                        useColumnStateSettingToggled={useColumnStateSettingToggled}
                                        setUseColumnStateSettingToggled={setUseColumnStateSettingToggled}
                                        toggleUpdateUseColumnState={toggleUpdateUseColumnState} />
                    </div>
                    <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                        <ClickToShowColumnOptionsWithToggleButtonGroup
                            columnMode={columnMode} setColumnMode={setColumnMode} gridColumnStateSessionVariableName={gridColumnStateSessionVariableName} gridApi={gridApi}
                            minColumnIds={minColumnIds} medColumnIds={medColumnIds} updateGridColumnModeFunction={null} disableMediumOption={false} />
                        <ClearRefresh gridApi = {gridApi} showRefreshIcon={false} sseDataPullActive={sseDataPullActive} showExcelExportIcon={true} excelExportFunction = {excelExport} />
                    </div>
                </div>
                <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                    <Grid
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        sideBar={sideBar}
                        rowSelection={rowSelection}
                        setGridApi={setGridApi}
                        sseDataPullActive={sseDataPullActive}
                        setSSEDataPullActive={setSSEDataPullActive}
                        asyncTransactionWaitMillis={asyncTransactionWaitMillis}
                        setAsyncTransactionWaitMillis={setAsyncTransactionWaitMillis}
                        excelExport={excelExport}
                        columnMode={columnMode}
                        setColumnMode={setColumnMode}
                        location={location}
                    />
                </div>
            </div>
        </div>
    );

    function toggleUpdateUseFilterState(toggleSetting){
        updateUseFilterStateHelper(toggleSetting, gridFilterStateSessionVariableName, null); //TODO: replace with endpoints for filter state
    }
    function toggleUpdateUseColumnState(toggleSetting){
        updateUseColumnStateHelper(toggleSetting, gridColumnStateSessionVariableName, null); //TODO: replace with endpoints for column state
    }
    function excelExport(){
        standardExcelExportHelper(gridApi, sseDataPullActive, "relatedDLLGridExport")
    }
}

class Grid extends Component {
    rowData = []
    updateTransactionsToApply = []
    abortController = new AbortController()

    constructor(props, onClickRow, filterVals) {
        super(props);
    }
    onFirstDataRendered = (params) => {

    };
    onColumnStateChanged = (params) => {
        //function to handle when column state changes: sort change, column visibility changes, or a column position on grid is moved
        //TODO: replace functions with api call
        if(params.source !== "api" && params.source !== "gridOptionsChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, null)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, null, customColumnModeText)
        }
        else if(params.source === "api" && params.type === "sortChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, null)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, null, customColumnModeText)
        }
    }

    componentWillUnmount(){
        this.abortController.abort()
    }

    populateGrid = async (rowData) => {
        standardHandlePopulateGrid(rowData, this.gridApi)
    }

    updateGridForChangeStream = async (changeStreamData) => {
        let operationType = changeStreamData.operationType
        let objectBody = changeStreamData.body
        if(operationType === "UPDATE" || operationType === "REPLACE"){
            standardHandleUpdateAndReplaceEvent(objectBody, this.gridApi, this.props.sseDataPullActive, this.updateTransactionsToApply)
        }
        else if (operationType === "INSERT"){
            standardHandleInsertEvent(objectBody, this.gridApi, this.props.sseDataPullActive)
        }
    }

    getRowId = (params) => {
        return params.data.id
    }

    getContextMenuItems = (params) => {
        let excelExport = this.props.excelExport //don't have access to this.props below in the action function so define it here
        return [
            standardExcelExportObjectInContextMenu(excelExport),
            "resetColumns",
            "autoSizeAll"
        ];
    };

    onGridReady = async (gridReadyParams) => {
        this.gridApi = gridReadyParams.api;
        this.props.setGridApi(gridReadyParams.api);

        let columnMode = this.props.columnMode
        //check which initial column mode to apply
        if(columnMode === customColumnModeText){
            onGridReadyHelperForColumnState(gridReadyParams, gridColumnStateSessionVariableName)
        }
        else if(columnMode === minColumnModeText){
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, gridReadyParams.api, this.props.setColumnMode, minColumnModeText, minColumnIds, null)
        }
        else if(columnMode === mediumColumnModeText){
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, gridReadyParams.api, this.props.setColumnMode, mediumColumnModeText, medColumnIds, null)
        }
        //else if columnMode is max then the default column state already shows the max amount of columns no need to update

        onGridReadyHelper(gridReadyParams, gridFilterStateSessionVariableName);

        let location = this.props.location
        let urlSearchParams = new URLSearchParams(location.search);
        let uniqueProcessIdentifier = urlSearchParams.get("uniqueProcessIdentifier")
        try{
            uniqueProcessIdentifier = JSON.parse(uniqueProcessIdentifier)
        } catch (e) {}
        let agentId = urlSearchParams.get("agentId")
        try{
            agentId = JSON.parse(agentId)
        } catch (e) {}
        let zenGroupId = urlSearchParams.get("zenGroupId")
        try{
            zenGroupId = JSON.parse(zenGroupId)
        } catch (e) {}
        let showAllTelemetry = urlSearchParams.get("showAllTelemetry")
        try{
            showAllTelemetry = JSON.parse(showAllTelemetry)
        } catch (e) {}
        if(showAllTelemetry === null || showAllTelemetry === undefined){
            showAllTelemetry = true //default to true, coming from creations and injections pages this will be blank, showAllTelemetry will only be false coming from incidents page
        }
        let body = JSON.stringify({
                trainingMode: getTrainingModeSetting(),
                demoMode: getDemoModeSetting(),
                zenGroupId: zenGroupId,
                agentId: agentId,
                uniqueProcessIdentifier: uniqueProcessIdentifier,
                showAllTelemetry: showAllTelemetry
            })

        await loadDataWithSSEAndStartChangeStreamListener("/getUntrustedModuleLoadsReactive", null,
            this.populateGrid, this.updateGridForChangeStream, gridReadyParams, this.props.setSSEDataPullActive, this.props.setAsyncTransactionWaitMillis, this.updateTransactionsToApply,
            this.abortController, null, null, body)
        //gridReadyParams.api.sizeColumnsToFit()
    };
    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="myGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            ClientSideRowModelModule,
                            AllCommunityModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule,
                            ExcelExportModule,
                            CsvExportModule
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        multiSortKey={"ctrl"}
                        rowData={this.rowData}
                        onGridReady={this.onGridReady}
                        maintainColumnOrder={true} //fixes issue where if you re-order/move column then click anywhere on the grid it reverts this change
                        asyncTransactionWaitMillis={this.props.asyncTransactionWaitMillis}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        getRowId={this.getRowId}
                        onCellEditingStopped={agentDisplayNameAndZenGroupDisplayNameOnlyCellEditingStopped}
                        rowSelection={this.props.rowSelection}
                        onSelectionChanged={() => {
                            const selectedRows = this.gridApi.getSelectedRows();
                            this.props.onClickRow && this.props.onClickRow(selectedRows);
                        }}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        onFirstDataRendered={this.onFirstDataRendered.bind(this)}
                        onFilterChanged={(params)=> {
                            //TODO: replace with endpoints for filter state
                            onFilterChangedHelper(params, gridFilterStateSessionVariableName, null);
                        }}
                        //columnState listeners
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        );
    }
}

export function convertDllTrustError(value){
    switch(value) {
        case "UNSIGNED":
            return "Unsigned"
        case "SIGNER_NOT_WHITELISTED":
            return "Untrusted Signature"
        case "TAMPERED_IN_MEMORY":
            return "Tampered in Memory"
        case "TAMPERED_ON_DISK":
            return "Tampered on Disk"
        case "GENERIC_ERROR":
            return "Generic Error"
        case "INVALID_ERROR":
            return "Invalid Error"
        default:
            return "";
    }
}